import React, { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import {
  listSubjects,
  getPeriodTimes,
  listAttendance,
  getAttendance,
  setAttendance,
  listExams,
  getExam,
  saveExam,
  deleteExam,
  setExamStatus,
} from "./happyschoolApi";
import { fetchCatalog } from "../quiz3_static/quiz3Utils";
import Quiz3TreeBrowser from "../quiz3_static/Quiz3TreeBrowser";

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// Ngày giờ dạng "dd/mm/yyyy HH:mm" (giờ máy người xem) cho khung thời gian bài kiểm tra.
const fmtDateTime = (ms) =>
  ms ? new Date(ms).toLocaleString("vi-VN", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";
// Trạng thái hiển thị: Đóng (GV đóng tay) > Chưa mở / Hết hạn (theo khung giờ) > Mở.
const examPhase = (ex, now = Date.now()) => {
  if (ex.status === "closed") return "closed";
  if (ex.startAt && now < ex.startAt) return "upcoming";
  if (ex.endAt && now > ex.endAt) return "expired";
  return "open";
};

// Rút gọn tên hiển thị ở danh sách vắng/muộn: chỉ hiện TÊN GỌI (từ cuối).
// Nếu trong lớp có nhiều HS trùng tên gọi -> thêm viết tắt họ + đệm (bỏ dấu,
// Đ->D, viết hoa) để phân biệt. VD "Đàm Duy Anh" -> "Anh", khi trùng -> "AnhDD".
const deAccentInitial = (w) =>
  String(w || "").normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/đ/g, "d").replace(/Đ/g, "D").charAt(0).toUpperCase();
const makeShortName = (students) => {
  const parts = (full) => String(full || "").trim().split(/\s+/).filter(Boolean);
  const given = (full) => { const p = parts(full); return p.length ? p[p.length - 1] : String(full || ""); };
  const count = {};
  (students || []).forEach((s) => { const g = given(s.name); count[g] = (count[g] || 0) + 1; });
  return (name) => {
    const p = parts(name);
    if (!p.length) return String(name || "");
    const g = p[p.length - 1];
    if ((count[g] || 0) <= 1) return g;
    return g + p.slice(0, -1).map(deAccentInitial).join("");
  };
};

const ATT = [
  { key: "co_mat", label: "Có mặt", short: "CM", color: "#059669", bg: "#ecfdf5" },
  { key: "muon", label: "Muộn", short: "Muộn", color: "#d97706", bg: "#fffbeb" },
  { key: "co_phep", label: "Vắng có phép", short: "P", color: "#b45309", bg: "#fffbeb" },
  { key: "khong_phep", label: "Vắng không phép", short: "KP", color: "#dc2626", bg: "#fef2f2" },
  { key: "vang", label: "Vắng", short: "Vắng", color: "#dc2626", bg: "#fef2f2" },
];
const ATT_BY = Object.fromEntries(ATT.map((a) => [a.key, a]));
const ABSENT_KEYS = ["co_phep", "khong_phep", "vang"]; // Muộn = có mặt (đi trễ), không tính vắng

// ============ ĐIỂM DANH ============
export function AttendanceModal({ schoolId, classId, className, students, onClose }) {
  const [date, setDate] = useState(todayStr());
  const [sessions, setSessions] = useState([]); // [{key,label,periods:[...]}]
  const [sessionKey, setSessionKey] = useState("S");
  const [tiet, setTiet] = useState(1);
  const [records, setRecords] = useState({}); // studentId -> status
  const [history, setHistory] = useState({}); // { date: { slot: {records} } }
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [absentOneLine, setAbsentOneLine] = useState(false); // false = nhiều dòng, true = 1 dòng cuộn ngang

  const slot = `${sessionKey}${tiet}`;
  const curSession = sessions.find((s) => s.key === sessionKey) || sessions[0];
  const tietCount = curSession ? (curSession.periods || []).length : 5;
  const sessLabel = (k) => { const s = sessions.find((x) => x.key === k); return (s && s.label) || (k === "S" ? "Sáng" : k === "C" ? "Chiều" : k); };

  const reloadHistory = () => listAttendance(schoolId, classId).then((d) => setHistory((d && d.days) || {})).catch(() => {});
  useEffect(() => { reloadHistory(); }, [schoolId, classId]); // eslint-disable-line

  // Danh sách buổi đã điểm danh (mới nhất trước) kèm số vắng.
  const doneList = [];
  Object.keys(history).sort((a, b) => b.localeCompare(a)).forEach((d) => {
    Object.keys(history[d] || {}).forEach((sl) => {
      const recs = (history[d][sl] || {}).records || {};
      const vals = Object.values(recs);
      const vang = vals.filter((v) => ABSENT_KEYS.includes(v)).length;
      doneList.push({ date: d, slot: sl, sk: sl[0], ti: sl.slice(1), vang, total: vals.length });
    });
  });
  const openSession = (date, sk, ti) => { setDate(date); setSessionKey(sk); setTiet(Number(ti) || 1); };

  useEffect(() => {
    getPeriodTimes(schoolId)
      .then((pt) => {
        const s = (pt && pt.sessions) || [];
        setSessions(s);
        if (s.length && !s.find((x) => x.key === sessionKey)) setSessionKey(s[0].key);
      })
      .catch(() => {});
  }, [schoolId]); // eslint-disable-line

  useEffect(() => {
    if (!schoolId || !classId || !date || !slot) return;
    setLoading(true); setMsg("");
    getAttendance(schoolId, classId, date, slot)
      .then((d) => setRecords((d && d.records) || {}))
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  }, [schoolId, classId, date, slot]);

  const statusOf = (id) => records[id] || "co_mat";
  const setStatus = (id, st) => setRecords((r) => ({ ...r, [id]: st }));
  const shortName = useMemo(() => makeShortName(students), [students]);

  const counts = useMemo(() => {
    const c = {}; ATT.forEach((a) => { c[a.key] = 0; });
    students.forEach((s) => { const st = statusOf(s.id); if (c[st] !== undefined) c[st] += 1; });
    return c;
  }, [records, students]); // eslint-disable-line

  const save = async () => {
    setSaving(true); setErr(""); setMsg("");
    try {
      // Ghi đủ trạng thái cho mọi HS (kể cả có mặt) để lưu lịch sử rõ ràng.
      const full = {};
      students.forEach((s) => { full[s.id] = statusOf(s.id); });
      await setAttendance(schoolId, classId, date, slot, full);
      const sLabel = (curSession && curSession.label) || sessionKey;
      setMsg(`Đã lưu điểm danh ${date} · ${sLabel} tiết ${tiet}`);
      reloadHistory();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal} onClick={(e) => e.stopPropagation()}>
        <div style={head}>
          <h3 style={{ margin: 0 }}>📋 Điểm danh — Lớp {className}</h3>
          <button onClick={onClose} style={xBtn}>✕</button>
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", margin: "8px 0 12px" }}>
          <label style={{ fontSize: 13, fontWeight: 600 }}>Ngày <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={inp} /></label>
          <label style={{ fontSize: 13, fontWeight: 600 }}>Buổi
            <select value={sessionKey} onChange={(e) => setSessionKey(e.target.value)} style={inp}>
              {(sessions.length ? sessions : [{ key: "S", label: "Sáng" }, { key: "C", label: "Chiều" }]).map((s) => (
                <option key={s.key} value={s.key}>{s.label || s.key}</option>
              ))}
            </select>
          </label>
          <label style={{ fontSize: 13, fontWeight: 600 }}>Tiết
            <select value={tiet} onChange={(e) => setTiet(Number(e.target.value))} style={inp}>
              {Array.from({ length: tietCount }, (_, i) => i + 1).map((n) => (<option key={n} value={n}>{n}</option>))}
            </select>
          </label>
        </div>
        <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap", margin: "0 0 8px" }}>
          {ATT.map((a) => (<span key={a.key} style={{ fontSize: 13, color: a.color }}>{a.label}: <b>{counts[a.key]}</b></span>))}
        </div>
        {(() => {
          const absent = students.filter((s) => ABSENT_KEYS.includes(statusOf(s.id)));
          const late = students.filter((s) => statusOf(s.id) === "muon");
          if (absent.length === 0 && late.length === 0) return <div style={{ margin: "0 0 12px", fontSize: 13, color: "#059669" }}>✓ Cả lớp có mặt đầy đủ.</div>;
          return (
            <div style={{ margin: "0 0 12px", display: "flex", flexDirection: "column", gap: 8 }}>
              {absent.length > 0 && (
                <div style={{ padding: 10, background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, display: "flex", alignItems: "center", gap: 6, flexWrap: absentOneLine ? "nowrap" : "wrap", overflowX: absentOneLine ? "auto" : "visible" }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#b91c1c", flexShrink: 0 }}>Không có mặt ({absent.length}):</span>
                  <button type="button" onClick={() => setAbsentOneLine((v) => !v)} title="Chuyển chế độ hiển thị"
                    style={{ fontSize: 11, padding: "2px 8px", borderRadius: 999, cursor: "pointer", flexShrink: 0, border: "1px solid #b91c1c", background: "#fff", color: "#b91c1c", fontWeight: 600 }}>
                    {absentOneLine ? "☰ Nhiều dòng" : "↔ 1 dòng"}
                  </button>
                  {absent.map((s) => { const a = ATT_BY[statusOf(s.id)]; return (
                    <span key={s.id} title={s.name} style={{ fontSize: 12, padding: "2px 8px", borderRadius: 999, whiteSpace: "nowrap", flexShrink: 0, background: "#fff", border: `1px solid ${a.color}`, color: a.color }}>
                      {shortName(s.name)}{a.key !== "vang" ? <span style={{ opacity: 0.7 }}> ({a.short})</span> : null}
                    </span>
                  ); })}
                </div>
              )}
              {late.length > 0 && (
                <div style={{ padding: 10, background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#b45309", marginBottom: 4 }}>Đi muộn ({late.length}):</div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "nowrap", overflowX: "auto", paddingBottom: 4 }}>
                    {late.map((s) => (<span key={s.id} title={s.name} style={{ fontSize: 12, padding: "2px 8px", borderRadius: 999, whiteSpace: "nowrap", flexShrink: 0, background: "#fff", border: "1px solid #d97706", color: "#d97706" }}>{shortName(s.name)}</span>))}
                  </div>
                </div>
              )}
            </div>
          );
        })()}
        {err && <p style={{ color: "#dc2626", margin: "4px 0" }}>{err}</p>}
        {msg && <p style={{ color: "#059669", margin: "4px 0" }}>{msg}</p>}
        {doneList.length > 0 && (
          <div style={{ margin: "0 0 10px" }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 4 }}>📚 Đã điểm danh ({doneList.length}) — bấm để xem lại:</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "nowrap", overflowX: "auto", paddingBottom: 4 }}>
              {doneList.map((d) => {
                const on = d.date === date && d.slot === slot;
                return (
                  <button key={d.date + d.slot} type="button" onClick={() => openSession(d.date, d.sk, d.ti)}
                    style={{ padding: "3px 8px", borderRadius: 999, fontSize: 12, cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0, border: on ? "1px solid #0ea5a3" : "1px solid #e5e7eb", background: on ? "#f0fdfa" : "#fff", color: on ? "#0f766e" : "#374151", fontWeight: on ? 700 : 500 }}
                    title={`Vắng ${d.vang}/${d.total}`}>
                    {d.date.slice(8, 10)}/{d.date.slice(5, 7)} · {sessLabel(d.sk)} T{d.ti}{d.vang ? ` · vắng ${d.vang}` : ""}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        <div style={{ overflowY: "auto", flex: 1, border: "1px solid #e5e7eb", borderRadius: 8 }}>
          {loading ? <p style={{ padding: 12 }}>Đang tải...</p> : (
            <table style={tbl}>
              <thead><tr><th style={th}>STT</th><th style={{ ...th, textAlign: "left" }}>Họ tên</th><th style={th}>Trạng thái</th></tr></thead>
              <tbody>
                {students.map((s, i) => (
                  <tr key={s.id}>
                    <td style={td}>{i + 1}</td>
                    <td style={{ ...td, textAlign: "left", fontWeight: 600 }}>{s.name}</td>
                    <td style={td}>
                      <div style={{ display: "inline-flex", gap: 4 }}>
                        {ATT.map((a) => {
                          const on = statusOf(s.id) === a.key;
                          return (
                            <button key={a.key} type="button" onClick={() => setStatus(s.id, a.key)}
                              style={{ padding: "3px 8px", borderRadius: 6, fontSize: 12, cursor: "pointer", border: on ? `1px solid ${a.color}` : "1px solid #e5e7eb", background: on ? a.bg : "#fff", color: on ? a.color : "#6b7280", fontWeight: on ? 700 : 400 }}>
                              {a.label}
                            </button>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div style={foot}>
          <button onClick={onClose} style={ghost}>Đóng</button>
          <button onClick={save} disabled={saving} style={primary}>{saving ? "Đang lưu..." : "Lưu điểm danh"}</button>
        </div>
      </div>
    </div>
  );
}

// ============ NHẬP ĐIỂM KIỂM TRA ============
export function ScoreModal({ schoolId, classId, className, students, onClose }) {
  const [subjects, setSubjects] = useState([]);
  const [exams, setExams] = useState([]);
  const [examId, setExamId] = useState(""); // "" = tạo mới
  const [subject, setSubject] = useState("");
  const [title, setTitle] = useState("");
  const [date, setDate] = useState(todayStr());
  const [scores, setScores] = useState({}); // studentId -> điểm
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  // Bài kiểm tra nguồn Quiz3 (chấm tự động, ma trận theo từng đề) không cho
  // nhập tay ở đây nữa — chỉ hiện bài nhập tay thủ công.
  const reloadExams = () => listExams(schoolId, classId).then((d) => setExams(((d && d.exams) || []).filter((ex) => ex.source !== "quiz3"))).catch((e) => setErr(e.message));

  useEffect(() => {
    listSubjects(schoolId).then(setSubjects).catch(() => {});
    reloadExams();
  }, [schoolId, classId]); // eslint-disable-line

  const loadExam = (id) => {
    setExamId(id); setMsg(""); setErr("");
    if (!id) { setSubject(""); setTitle(""); setDate(todayStr()); setScores({}); return; }
    setLoading(true);
    getExam(schoolId, classId, id)
      .then((d) => {
        const m = (d && d.meta) || {};
        setSubject(m.subject || ""); setTitle(m.title || ""); setDate(m.date || todayStr());
        setScores((d && d.records) || {});
      })
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  };

  const save = async () => {
    if (!title.trim()) { setErr("Nhập tên bài kiểm tra."); return; }
    setSaving(true); setErr(""); setMsg("");
    const id = examId || `exam_${Date.now()}`;
    try {
      const records = {};
      students.forEach((s) => { const v = scores[s.id]; if (v !== undefined && String(v).trim() !== "") records[s.id] = String(v).trim(); });
      await saveExam(schoolId, classId, id, { subject: subject.trim(), title: title.trim(), date, records });
      setExamId(id);
      await reloadExams();
      setMsg("Đã lưu điểm bài: " + title);
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const removeExam = async () => {
    if (!examId || !window.confirm("Xoá bài kiểm tra này và toàn bộ điểm?")) return;
    try { await deleteExam(schoolId, classId, examId); loadExam(""); await reloadExams(); }
    catch (e) { setErr(e.message); }
  };

  // Xuất bảng điểm hiện tại ra file Excel (.xlsx). Dùng đúng điểm đang hiển thị.
  const exportExcel = () => {
    setErr(""); setMsg("");
    const rows = students.map((s, i) => ({
      STT: i + 1,
      "Họ tên": s.name,
      "Điểm": scores[s.id] !== undefined && String(scores[s.id]).trim() !== "" ? String(scores[s.id]).trim() : "",
    }));
    const header = [
      [`Bảng điểm — Lớp ${className}`],
      [`Môn: ${subject || "—"}`, `Bài: ${title || "—"}`, `Ngày: ${date || "—"}`],
      [],
    ];
    const ws = XLSX.utils.aoa_to_sheet(header);
    XLSX.utils.sheet_add_json(ws, rows, { origin: -1 });
    ws["!cols"] = [{ wch: 6 }, { wch: 32 }, { wch: 8 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Điểm");
    const safe = (t) => String(t || "").normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/đ/gi, "d").replace(/[^a-zA-Z0-9]+/g, "_").replace(/^_+|_+$/g, "");
    const fname = `Diem_${safe(className)}${title ? "_" + safe(title) : ""}${date ? "_" + date : ""}.xlsx`;
    XLSX.writeFile(wb, fname);
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal} onClick={(e) => e.stopPropagation()}>
        <div style={head}>
          <h3 style={{ margin: 0 }}>📝 Nhập điểm kiểm tra — Lớp {className}</h3>
          <button onClick={onClose} style={xBtn}>✕</button>
        </div>
        <p style={{ fontSize: 12, color: "#6b7280", margin: "4px 0 8px" }}>
          Chỉ dùng để nhập điểm <b>thủ công</b>. Bài kiểm tra tạo qua Quiz3 (chấm tự động) xem ở mục "Danh sách bài kiểm tra".
        </p>
        <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap", margin: "8px 0 10px" }}>
          <label style={lbl}>Bài KT
            <select value={examId} onChange={(e) => loadExam(e.target.value)} style={inp}>
              <option value="">+ Tạo bài mới</option>
              {exams.map((ex) => (<option key={ex.id} value={ex.id}>{ex.title}{ex.subject ? ` (${ex.subject})` : ""}{ex.date ? ` · ${ex.date}` : ""}</option>))}
            </select>
          </label>
          <label style={lbl}>Môn
            <select value={subject} onChange={(e) => setSubject(e.target.value)} style={inp}>
              <option value="">—</option>
              {subjects.map((s) => (<option key={s.id} value={s.name}>{s.name}</option>))}
            </select>
          </label>
          <label style={lbl}>Tên bài <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="VD: KT 15 phút" style={inp} /></label>
          <label style={lbl}>Ngày <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={inp} /></label>
          {examId && <button onClick={removeExam} style={{ ...ghost, color: "#dc2626", borderColor: "#fecaca" }}>Xoá bài</button>}
        </div>
        {err && <p style={{ color: "#dc2626", margin: "4px 0" }}>{err}</p>}
        {msg && <p style={{ color: "#059669", margin: "4px 0" }}>{msg}</p>}
        <div style={{ overflowY: "auto", flex: 1, border: "1px solid #e5e7eb", borderRadius: 8 }}>
          {loading ? <p style={{ padding: 12 }}>Đang tải...</p> : (
            <table style={tbl}>
              <thead><tr><th style={th}>STT</th><th style={{ ...th, textAlign: "left" }}>Họ tên</th><th style={th}>Điểm</th></tr></thead>
              <tbody>
                {students.map((s, i) => (
                  <tr key={s.id}>
                    <td style={td}>{i + 1}</td>
                    <td style={{ ...td, textAlign: "left", fontWeight: 600 }}>{s.name}</td>
                    <td style={td}>
                      <input value={scores[s.id] || ""} onChange={(e) => setScores((r) => ({ ...r, [s.id]: e.target.value }))}
                        placeholder="—" style={{ width: 70, padding: 4, textAlign: "center", border: "1px solid #d1d5db", borderRadius: 6 }} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div style={foot}>
          <button onClick={onClose} style={ghost}>Đóng</button>
          <button onClick={exportExcel} disabled={loading || !students.length} style={{ ...ghost, color: "#047857", borderColor: "#a7f3d0" }}>⬇️ Xuất Excel</button>
          <button onClick={save} disabled={saving} style={primary}>{saving ? "Đang lưu..." : "Lưu điểm"}</button>
        </div>
      </div>
    </div>
  );
}

// ============ MỞ BÀI KIỂM TRA (chọn nhiều chủ đề Quiz3 qua cây) ============
// Ma trận Học sinh × Bài: mỗi chủ đề tích chọn (mọi đề gắn ở node đó) sẽ là 1
// CỘT riêng trong bảng điểm — HS làm đề nào, điểm+tên đề đẩy vào đúng cột đó.
export function QuizExamModal({ schoolId, classId, className, onClose }) {
  const [quiz3Data, setQuiz3Data] = useState({ subjects: {}, topics: {}, quizzes: {} });
  const [checkedKeys, setCheckedKeys] = useState(new Set());
  const [title, setTitle] = useState("");
  const [date, setDate] = useState(todayStr());
  const [displayMode, setDisplayMode] = useState("both"); // "both" = hiện thường, "animated" = chống chụp ảnh AI
  // Khung thời gian làm bài ("YYYY-MM-DDTHH:mm" theo giờ máy GV) — để trống = không giới hạn.
  const [startAt, setStartAt] = useState("");
  const [endAt, setEndAt] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    // Chỉ cần cây môn/chủ đề + danh sách đề -> catalog nhẹ thay cho /api/all.
    fetchCatalog()
      .then((d) => setQuiz3Data({ subjects: d.subjects || {}, topics: d.topics || {}, quizzes: d.quizzes || {} }))
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  }, []);

  const toggleCheck = (nodeKey) => {
    setCheckedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(nodeKey)) next.delete(nodeKey); else next.add(nodeKey);
      return next;
    });
  };

  // Node đã tích -> danh sách đề (quizId) gắn TRỰC TIẾP ở đúng node đó.
  const selectedQuizzes = useMemo(() => {
    const list = [];
    Object.entries(quiz3Data.quizzes).forEach(([id, q]) => {
      if (q.closed) return; // đề đã đóng (vd đề in giấy) không tính vào bài kiểm tra online
      const key = q.topicId ? `topic:${q.topicId}` : `subject:${q.subjectId}`;
      if (checkedKeys.has(key)) list.push({ id, title: q.title || q.name || "(không tên)" });
    });
    return list;
  }, [quiz3Data.quizzes, checkedKeys]);

  const save = async () => {
    if (!title.trim()) { setErr("Nhập tên bài kiểm tra."); return; }
    if (!selectedQuizzes.length) { setErr("Tích chọn ít nhất 1 chủ đề/đề trên cây bên dưới."); return; }
    const startMs = startAt ? new Date(startAt).getTime() : null;
    const endMs = endAt ? new Date(endAt).getTime() : null;
    if (startMs && endMs && endMs <= startMs) { setErr("Thời gian kết thúc phải sau thời gian bắt đầu."); return; }
    setSaving(true); setErr(""); setMsg("");
    try {
      const id = `exam_${Date.now()}`;
      await saveExam(schoolId, classId, id, {
        title: title.trim(), date, autoFromQuiz: true, displayMode,
        quizIds: selectedQuizzes.map((q) => q.id),
        startAt: startMs, endAt: endMs,
      });
      const range = startMs || endMs
        ? ` Thời gian làm bài: ${startMs ? "từ " + fmtDateTime(startMs) : ""}${endMs ? " đến " + fmtDateTime(endMs) : ""}.`
        : "";
      setMsg(`Đã mở bài kiểm tra với ${selectedQuizzes.length} đề. Học sinh lớp làm đề nào trong số đó, điểm + tên đề sẽ tự đẩy vào đúng cột.${range}`);
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={{ ...modal, maxWidth: 640 }} onClick={(e) => e.stopPropagation()}>
        <div style={head}>
          <h3 style={{ margin: 0 }}>🧪 Mở bài kiểm tra (Quiz3) — Lớp {className}</h3>
          <button onClick={onClose} style={xBtn}>✕</button>
        </div>
        <p style={{ fontSize: 12, color: "#6b7280", margin: "6px 0 12px" }}>
          Tích chọn các <b>chủ đề</b> trên cây — mọi đề gắn ở chủ đề đó học sinh sẽ được làm. Mỗi đề là 1 cột điểm riêng trong bảng.
        </p>
        {err && <p style={{ color: "#dc2626", margin: "4px 0" }}>{err}</p>}
        {msg && <p style={{ color: "#059669", margin: "4px 0" }}>{msg}</p>}
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
          <label style={lbl}>Tên bài kiểm tra
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="VD: KT giữa kỳ - HTML" style={{ ...inp, width: 260 }} />
          </label>
          <label style={lbl}>Ngày
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={inp} />
          </label>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 4 }}>
          <label style={lbl}>Bắt đầu (start date)
            <input type="datetime-local" value={startAt} onChange={(e) => setStartAt(e.target.value)} style={inp} />
          </label>
          <label style={lbl}>Kết thúc (end date)
            <input type="datetime-local" value={endAt} min={startAt || undefined} onChange={(e) => setEndAt(e.target.value)} style={inp} />
          </label>
        </div>
        <p style={{ fontSize: 12, color: "#6b7280", margin: "0 0 10px" }}>
          Để trống = không giới hạn. Ngoài khoảng thời gian này học sinh không thấy bài trong danh sách nhắc và điểm nộp sẽ không được tính.
        </p>
        <div style={{ marginBottom: 10, fontSize: 13 }}>
          <div style={{ fontWeight: 600, color: "#374151", marginBottom: 4 }}>Kiểu hiển thị đề khi học sinh làm bài</div>
          <label style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
            <input type="radio" checked={displayMode === "both"} onChange={() => setDisplayMode("both")} />
            Đề thường: hiện cả câu hỏi và đáp án cùng lúc
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <input type="radio" checked={displayMode === "animated"} onChange={() => setDisplayMode("animated")} />
            Chống chụp ảnh AI: câu hỏi chạy dần, xong mới hiện các đáp án
          </label>
        </div>
        <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: 10, maxHeight: 260, overflowY: "auto", marginBottom: 10 }}>
          {loading ? <p style={{ margin: 0, color: "#6b7280" }}>Đang tải cây môn học...</p> : (
            <Quiz3TreeBrowser checkable checkedKeys={checkedKeys} onToggleCheck={toggleCheck} />
          )}
        </div>
        <div style={{ fontSize: 12, color: "#374151", marginBottom: 10 }}>
          Đã chọn <b>{selectedQuizzes.length}</b> đề{selectedQuizzes.length > 0 && ": " + selectedQuizzes.map((q) => q.title).join(", ")}
        </div>
        <div style={foot}>
          <button onClick={onClose} style={ghost}>Đóng</button>
          <button onClick={save} disabled={saving} style={primary}>{saving ? "Đang mở..." : "Mở bài kiểm tra"}</button>
        </div>
      </div>
    </div>
  );
}

// ============ XEM MA TRẬN ĐIỂM (Học sinh × Bài) — bài kiểm tra Quiz3 ============
export function QuizExamMatrixModal({ schoolId, classId, className, examId, examTitle, students, onClose }) {
  const [meta, setMeta] = useState(null);
  const [records, setRecords] = useState({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [absentIds, setAbsentIds] = useState(new Set());

  useEffect(() => {
    setLoading(true); setErr("");
    getExam(schoolId, classId, examId)
      .then((d) => { setMeta((d && d.meta) || {}); setRecords((d && d.records) || {}); })
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  }, [schoolId, classId, examId]);

  // Học sinh vắng đúng ngày làm bài (meta.date) -> bôi vàng ở bảng điểm. Vắng
  // nếu bất kỳ tiết nào trong ngày đó có trạng thái thuộc ABSENT_KEYS.
  useEffect(() => {
    const date = meta && meta.date;
    if (!date) { setAbsentIds(new Set()); return; }
    listAttendance(schoolId, classId)
      .then((d) => {
        const dayRecord = ((d && d.days) || {})[date] || {};
        const ids = new Set();
        Object.values(dayRecord).forEach((slot) => {
          Object.entries((slot && slot.records) || {}).forEach(([sid, status]) => {
            if (ABSENT_KEYS.includes(status)) ids.add(sid);
          });
        });
        setAbsentIds(ids);
      })
      .catch(() => setAbsentIds(new Set()));
  }, [schoolId, classId, meta]);

  // Cột = danh sách quizId đã chọn khi mở bài; tên cột lấy từ điểm đã đẩy về
  // (records/{sid}/{quizId}.title) nếu có, học sinh nào chưa làm thì để "—".
  const columns = useMemo(() => {
    const quizIds = (meta && meta.quizIds) || [];
    return quizIds.map((qid) => {
      let title = qid;
      for (const sid of Object.keys(records)) {
        const cell = records[sid] && records[sid][qid];
        if (cell && cell.title) { title = cell.title; break; }
      }
      return { qid, title };
    });
  }, [meta, records]);

  // Điểm TB = tổng điểm các đề / tổng số đề trong bài — đề nào chưa làm tính
  // là 0 (không bỏ qua), để phản ánh đúng tiến độ làm bài của học sinh.
  const avgOf = (studentId) => {
    if (!columns.length) return null;
    const sum = columns.reduce((acc, c) => {
      const cell = records[studentId] && records[studentId][c.qid];
      return acc + (cell ? Number(cell.score) || 0 : 0);
    }, 0);
    return Math.round((sum / columns.length) * 10) / 10;
  };

  const exportExcel = () => {
    const rows = students.map((s, i) => {
      const row = { STT: i + 1, "Họ tên": s.name };
      columns.forEach((c) => {
        const cell = records[s.id] && records[s.id][c.qid];
        row[c.title] = cell ? cell.score : "";
      });
      row["Điểm TB"] = avgOf(s.id);
      return row;
    });
    const header = [[`Bảng điểm — Lớp ${className}`], [`Bài: ${examTitle || "—"}`], []];
    const ws = XLSX.utils.aoa_to_sheet(header);
    XLSX.utils.sheet_add_json(ws, rows, { origin: -1 });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Điểm");
    const safe = (t) => String(t || "").normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/đ/gi, "d").replace(/[^a-zA-Z0-9]+/g, "_").replace(/^_+|_+$/g, "");
    XLSX.writeFile(wb, `Diem_${safe(className)}_${safe(examTitle)}.xlsx`);
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={{ ...modal, maxWidth: 920 }} onClick={(e) => e.stopPropagation()}>
        <div style={head}>
          <h3 style={{ margin: 0 }}>📊 {examTitle} — Lớp {className}</h3>
          <button onClick={onClose} style={xBtn}>✕</button>
        </div>
        {err && <p style={{ color: "#dc2626", margin: "4px 0" }}>{err}</p>}
        {!loading && columns.length === 0 && <p style={{ color: "#6b7280" }}>Bài này chưa gắn đề nào.</p>}
        <div style={{ overflow: "auto", flex: 1, border: "1px solid #e5e7eb", borderRadius: 8 }}>
          {loading ? <p style={{ padding: 12 }}>Đang tải...</p> : (
            <table style={tbl}>
              <thead>
                <tr>
                  <th style={th}>STT</th>
                  <th style={{ ...th, textAlign: "left" }}>Họ tên</th>
                  {columns.map((c) => (<th key={c.qid} style={th}>{c.title}</th>))}
                  {columns.length > 0 && <th style={{ ...th, background: "#f0fdfa" }}>Điểm TB</th>}
                </tr>
              </thead>
              <tbody>
                {students.map((s, i) => {
                  const absent = absentIds.has(s.id);
                  const rowBg = absent ? "#fef9c3" : undefined;
                  return (
                    <tr key={s.id} title={absent ? "Vắng ngày làm bài" : undefined}>
                      <td style={{ ...td, background: rowBg }}>{i + 1}</td>
                      <td style={{ ...td, textAlign: "left", fontWeight: 600, background: rowBg }}>{s.name}{absent ? " 🟡" : ""}</td>
                      {columns.map((c) => {
                        const cell = records[s.id] && records[s.id][c.qid];
                        return <td key={c.qid} style={{ ...td, background: rowBg }}>{cell ? cell.score : "—"}</td>;
                      })}
                      {columns.length > 0 && <td style={{ ...td, fontWeight: 700, background: absent ? rowBg : "#f0fdfa" }}>{avgOf(s.id)}</td>}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        <div style={foot}>
          <button onClick={onClose} style={ghost}>Đóng</button>
          <button onClick={exportExcel} disabled={loading || !students.length} style={{ ...ghost, color: "#047857", borderColor: "#a7f3d0" }}>⬇️ Xuất Excel</button>
        </div>
      </div>
    </div>
  );
}

// ============ DANH SÁCH BÀI KIỂM TRA (mở/đóng) ============
export function ExamListModal({ schoolId, classId, className, onClose, onViewMatrix }) {
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");

  const reload = () => {
    setLoading(true);
    listExams(schoolId, classId)
      .then((d) => setExams(((d && d.exams) || []).sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0))))
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  };
  useEffect(reload, [schoolId, classId]); // eslint-disable-line

  const toggle = async (ex) => {
    const next = ex.status === "closed" ? "open" : "closed";
    setBusy(ex.id); setErr("");
    try { await setExamStatus(schoolId, classId, ex.id, next); reload(); }
    catch (e) { setErr(e.message); } finally { setBusy(""); }
  };
  const remove = async (ex) => {
    if (!window.confirm(`Xoá bài kiểm tra "${ex.title}" và toàn bộ điểm?`)) return;
    setBusy(ex.id); setErr("");
    try { await deleteExam(schoolId, classId, ex.id); reload(); }
    catch (e) { setErr(e.message); } finally { setBusy(""); }
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={{ ...modal, maxWidth: 760 }} onClick={(e) => e.stopPropagation()}>
        <div style={head}>
          <h3 style={{ margin: 0 }}>📋 Bài kiểm tra — Lớp {className}</h3>
          <button onClick={onClose} style={xBtn}>✕</button>
        </div>
        <p style={{ fontSize: 12, color: "#6b7280", margin: "6px 0 10px" }}>
          Bài <b style={{ color: "#059669" }}>Mở</b> thì học sinh làm Quiz3 xong điểm sẽ đẩy vào. Bấm <b>Đóng</b> để ngừng nhận điểm. Bài có thời gian làm bài chỉ nhận điểm trong khoảng đó.
        </p>
        {err && <p style={{ color: "#dc2626", margin: "4px 0" }}>{err}</p>}
        <div style={{ overflowY: "auto", flex: 1, border: "1px solid #e5e7eb", borderRadius: 8 }}>
          {loading ? <p style={{ padding: 12 }}>Đang tải...</p> : exams.length === 0 ? (
            <p style={{ padding: 12, color: "#6b7280" }}>Lớp này chưa có bài kiểm tra nào.</p>
          ) : (
            <table style={tbl}>
              <thead><tr>
                <th style={th}>STT</th><th style={{ ...th, textAlign: "left" }}>Tên bài</th><th style={th}>Môn</th>
                <th style={th}>Ngày</th><th style={th}>Thời gian làm bài</th><th style={th}>Nguồn</th><th style={th}>Đã có điểm</th><th style={th}>Trạng thái</th><th style={th}>Thao tác</th>
              </tr></thead>
              <tbody>
                {exams.map((ex, i) => {
                  const closed = ex.status === "closed";
                  const phase = examPhase(ex);
                  const badge = {
                    closed: { text: "Đóng", bg: "#f3f4f6", fg: "#6b7280", bd: "#d1d5db" },
                    upcoming: { text: "Chưa mở", bg: "#eff6ff", fg: "#2563eb", bd: "#bfdbfe" },
                    expired: { text: "Hết hạn", bg: "#fef2f2", fg: "#dc2626", bd: "#fecaca" },
                    open: { text: "Mở", bg: "#ecfdf5", fg: "#059669", bd: "#a7f3d0" },
                  }[phase];
                  return (
                    <tr key={ex.id}>
                      <td style={td}>{i + 1}</td>
                      <td style={{ ...td, textAlign: "left", fontWeight: 600 }}>{ex.title}</td>
                      <td style={td}>{ex.subject || "—"}</td>
                      <td style={td}>{ex.date || ""}</td>
                      <td style={{ ...td, fontSize: 12, whiteSpace: "nowrap" }}>
                        {ex.startAt || ex.endAt ? (
                          <>{ex.startAt ? fmtDateTime(ex.startAt) : "…"}<br />→ {ex.endAt ? fmtDateTime(ex.endAt) : "…"}</>
                        ) : "—"}
                      </td>
                      <td style={td}>{ex.source === "quiz3" ? "Quiz3" : "Nhập tay"}</td>
                      <td style={td}>{ex.recordCount || 0}</td>
                      <td style={td}>
                        <span style={{ padding: "2px 8px", borderRadius: 999, fontSize: 12, fontWeight: 700, background: badge.bg, color: badge.fg, border: `1px solid ${badge.bd}` }}>
                          {badge.text}
                        </span>
                      </td>
                      <td style={{ ...td, whiteSpace: "nowrap" }}>
                        {ex.source === "quiz3" && onViewMatrix && (
                          <button onClick={() => onViewMatrix(ex.id, ex.title)} style={{ ...ghost, padding: "4px 8px", marginRight: 4 }}>📊 Xem điểm</button>
                        )}
                        <button disabled={busy === ex.id} onClick={() => toggle(ex)} style={closed ? openBtn : closeBtn}>{closed ? "Mở lại" : "Đóng"}</button>{" "}
                        <button disabled={busy === ex.id} onClick={() => remove(ex)} style={{ ...ghost, color: "#dc2626", borderColor: "#fecaca", padding: "4px 8px" }}>🗑</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        <div style={foot}><button onClick={onClose} style={ghost}>Đóng</button></div>
      </div>
    </div>
  );
}

const openBtn = { padding: "4px 10px", background: "#0ea5a3", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 12 };
const closeBtn = { padding: "4px 10px", background: "#fff", color: "#b45309", border: "1px solid #fcd34d", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 12 };

const overlay = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 };
const modal = { background: "#fff", borderRadius: 12, padding: 20, width: "min(720px, 100%)", maxHeight: "90vh", display: "flex", flexDirection: "column", boxShadow: "0 10px 40px rgba(0,0,0,0.2)" };
const head = { display: "flex", justifyContent: "space-between", alignItems: "center" };
const foot = { display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 };
const xBtn = { border: "none", background: "transparent", fontSize: 18, cursor: "pointer", color: "#6b7280" };
const lbl = { fontSize: 12, fontWeight: 600, color: "#4b5563", display: "flex", flexDirection: "column", gap: 3 };
const inp = { padding: 6, border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13 };
const tbl = { borderCollapse: "collapse", width: "100%", fontSize: 13 };
const th = { border: "1px solid #e5e7eb", padding: "5px 8px", background: "#f9fafb", position: "sticky", top: 0, textAlign: "center" };
const td = { border: "1px solid #e5e7eb", padding: "4px 8px", textAlign: "center" };
const ghost = { padding: "7px 14px", background: "#f3f4f6", color: "#374151", border: "1px solid #d1d5db", borderRadius: 6, cursor: "pointer" };
const primary = { padding: "7px 16px", background: "#0ea5a3", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600 };
